/**
 * Panel bileşen kiti (docs/56 Faz 0): "önce özet, sonra detay". Sayfalar bu
 * parçalarla kurulur; renkler yalnız tema değişkenlerinden (theme.css).
 */
import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { DialogPanel, trapDialogFocus } from '../ui';

/** Telefon genişliği (767 px ve altı); değişince yeniden çizer. */
export function useTelefon() {
  const sorgu = '(max-width: 767px)';
  const [telefon, setTelefon] = useState(() =>
    typeof matchMedia === 'function' ? matchMedia(sorgu).matches : false,
  );
  useEffect(() => {
    if (typeof matchMedia !== 'function') return;
    const m = matchMedia(sorgu);
    const degisti = () => setTelefon(m.matches);
    m.addEventListener('change', degisti);
    return () => m.removeEventListener('change', degisti);
  }, []);
  return telefon;
}

export type Ton = 'notr' | 'ok' | 'warn' | 'danger' | 'info' | 'accent';

/** Sayfa iskeleti: başlık, tek satır açıklama, sağda eylemler. */
export function Sayfa({
  baslik,
  aciklama,
  eylemler,
  children,
}: {
  baslik: string;
  aciklama?: ReactNode;
  eylemler?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="k-sayfa">
      <header className="k-sayfa-bas">
        <div>
          <h1>{baslik}</h1>
          {aciklama && <p className="k-soluk">{aciklama}</p>}
        </div>
        {eylemler && <div className="k-eylemler">{eylemler}</div>}
      </header>
      {children}
    </div>
  );
}

/** Kart: başlık, sağ üstte tek eylem, gövde. */
export function Kart({
  baslik,
  alt,
  eylem,
  children,
  sikisik = false,
}: {
  baslik?: ReactNode;
  alt?: ReactNode;
  eylem?: ReactNode;
  children?: ReactNode;
  sikisik?: boolean;
}) {
  return (
    <section className={`k-kart${sikisik ? ' sikisik' : ''}`}>
      {(baslik || eylem) && (
        <header className="k-kart-bas">
          <div>
            {baslik && <h2>{baslik}</h2>}
            {alt && <p className="k-soluk">{alt}</p>}
          </div>
          {eylem}
        </header>
      )}
      {children}
    </section>
  );
}

/** Sayaç: tek bakışta sayı + kısa açıklama; tıklanabilirse ilgili yere götürür. */
export function Sayac({
  etiket,
  deger,
  ipucu,
  ton = 'notr',
  onClick,
}: {
  etiket: string;
  deger: ReactNode;
  ipucu?: ReactNode;
  ton?: Ton;
  onClick?: () => void;
}) {
  const icerik = (
    <>
      <span className="k-sayac-etiket">{etiket}</span>
      <strong className="k-sayac-deger">{deger}</strong>
      {ipucu && <span className="k-sayac-ipucu">{ipucu}</span>}
    </>
  );
  return onClick ? (
    <button type="button" className={`k-sayac ton-${ton} tiklanir`} onClick={onClick}>
      {icerik}
    </button>
  ) : (
    <div className={`k-sayac ton-${ton}`}>{icerik}</div>
  );
}

export function SayacSirasi({ children }: { children: ReactNode }) {
  return <div className="k-sayaclar">{children}</div>;
}

export function Rozet({ ton = 'notr', children }: { ton?: Ton; children: ReactNode }) {
  return <span className={`k-rozet ton-${ton}`}>{children}</span>;
}

/** Tanım listesi (etiket → değer), iki sütun; telefonda alt alta. */
export function Tanimlar({ satirlar }: { satirlar: [ReactNode, ReactNode][] }) {
  return (
    <dl className="k-tanimlar">
      {satirlar.map(([e, d], i) => (
        <div key={i}>
          <dt>{e}</dt>
          <dd>{d}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Katlanır bölüm: ayrıntı varsayılan kapalı. */
export function Katlanir({
  baslik,
  ozet,
  acik = false,
  children,
}: {
  baslik: ReactNode;
  ozet?: ReactNode;
  acik?: boolean;
  children: ReactNode;
}) {
  return (
    <details className="k-katlanir" open={acik}>
      <summary>
        <span>{baslik}</span>
        {ozet && <span className="k-soluk">{ozet}</span>}
      </summary>
      <div className="k-katlanir-govde">{children}</div>
    </details>
  );
}

export type Sekme = { id: string; ad: string; sayi?: number | null; uyari?: boolean };

/** Sekmeler: seçili sekmenin içeriği çağıranda; sayı rozetiyle. */
export function Sekmeler({
  sekmeler,
  secili,
  onSec,
  etiket,
}: {
  sekmeler: Sekme[];
  secili: string;
  onSec: (id: string) => void;
  etiket: string;
}) {
  return (
    <div className="k-sekmeler" role="tablist" aria-label={etiket}>
      {sekmeler.map((s) => (
        <button
          key={s.id}
          role="tab"
          type="button"
          aria-selected={s.id === secili}
          className={s.id === secili ? 'secili' : ''}
          onClick={() => onSec(s.id)}
        >
          {s.ad}
          {s.sayi != null && s.sayi > 0 && (
            <span className={`k-sekme-sayi${s.uyari ? ' uyari' : ''}`}>{s.sayi}</span>
          )}
        </button>
      ))}
    </div>
  );
}

export type Sutun<T> = {
  baslik: string;
  hucre: (satir: T) => ReactNode;
  /** Sıralama anahtarı; verilirse başlık tıklanır. */
  sirala?: (satir: T) => number | string;
  hizala?: 'sag';
  /** Telefonda gizlenir (karta sığmayan ikincil bilgi). */
  ikincil?: boolean;
};

/**
 * Veri tablosu: sayfalı (varsayılan 25), sıralanabilir; satıra tıklanınca
 * `onSatir` (genelde yan detay). Telefonda her satır etiketli kart.
 * Sunucu sayfalaması için `dahaFazla` verilir (imleçli "Daha fazla yükle").
 */
export function VeriTablosu<T>({
  sutunlar,
  satirlar,
  anahtar,
  sayfaBoyu = 25,
  onSatir,
  bos = 'Kayıt yok.',
  dahaFazla,
  etiket,
}: {
  sutunlar: Sutun<T>[];
  satirlar: readonly T[];
  anahtar: (satir: T) => string;
  sayfaBoyu?: number;
  onSatir?: (satir: T) => void;
  bos?: ReactNode;
  dahaFazla?: { yukleniyor: boolean; onYukle: () => void } | null;
  etiket: string;
}) {
  // Telefonda satırlar kart olur; sayfa kısa kalsın diye en çok 10 kart
  const telefon = useTelefon();
  if (telefon) sayfaBoyu = Math.min(sayfaBoyu, 10);
  const [sira, setSira] = useState<{ i: number; azalan: boolean } | null>(null);
  const [sayfa, setSayfa] = useState(0);
  const sirali = useMemo(() => {
    if (!sira) return satirlar;
    const f = sutunlar[sira.i]?.sirala;
    if (!f) return satirlar;
    const kopya = [...satirlar].sort((a, b) => {
      const x = f(a);
      const y = f(b);
      return x < y ? -1 : x > y ? 1 : 0;
    });
    return sira.azalan ? kopya.reverse() : kopya;
  }, [satirlar, sira, sutunlar]);
  const sayfaSayisi = Math.max(1, Math.ceil(sirali.length / sayfaBoyu));
  useEffect(() => {
    if (sayfa >= sayfaSayisi) setSayfa(0);
  }, [sayfa, sayfaSayisi]);
  const gorunen = sirali.slice(sayfa * sayfaBoyu, (sayfa + 1) * sayfaBoyu);
  if (satirlar.length === 0) return <p className="k-bos">{bos}</p>;
  return (
    <div className="k-tablo-kap">
      <div className="k-tablo-kaydir">
        <table className="k-tablo" aria-label={etiket}>
          <thead>
            <tr>
              {sutunlar.map((s, i) => (
                <th
                  key={s.baslik}
                  scope="col"
                  className={`${s.hizala === 'sag' ? 'sag' : ''}${s.ikincil ? ' ikincil' : ''}`}
                  aria-sort={sira?.i === i ? (sira.azalan ? 'descending' : 'ascending') : undefined}
                >
                  {s.sirala ? (
                    <button
                      type="button"
                      onClick={() =>
                        setSira((o) =>
                          o?.i === i ? { i, azalan: !o.azalan } : { i, azalan: true },
                        )
                      }
                    >
                      {s.baslik}
                      <span aria-hidden="true">
                        {sira?.i === i ? (sira.azalan ? ' ↓' : ' ↑') : ''}
                      </span>
                    </button>
                  ) : (
                    s.baslik
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {gorunen.map((r) => (
              <tr
                key={anahtar(r)}
                className={onSatir ? 'tiklanir' : undefined}
                tabIndex={onSatir ? 0 : undefined}
                onClick={onSatir ? () => onSatir(r) : undefined}
                onKeyDown={
                  onSatir
                    ? (e) => {
                        if (e.key === 'Enter') onSatir(r);
                      }
                    : undefined
                }
              >
                {sutunlar.map((s) => (
                  <td
                    key={s.baslik}
                    data-label={s.baslik}
                    className={`${s.hizala === 'sag' ? 'sag' : ''}${s.ikincil ? ' ikincil' : ''}`}
                  >
                    {s.hucre(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(sayfaSayisi > 1 || dahaFazla) && (
        <nav className="k-sayfalama" aria-label={`${etiket} sayfaları`}>
          <span className="k-soluk">
            {sirali.length} kayıt · sayfa {sayfa + 1}/{sayfaSayisi}
          </span>
          <div className="k-eylemler">
            <button type="button" disabled={sayfa === 0} onClick={() => setSayfa((s) => s - 1)}>
              ‹ Önceki
            </button>
            <button
              type="button"
              disabled={sayfa >= sayfaSayisi - 1}
              onClick={() => setSayfa((s) => s + 1)}
            >
              Sonraki ›
            </button>
            {dahaFazla && (
              <button type="button" disabled={dahaFazla.yukleniyor} onClick={dahaFazla.onYukle}>
                {dahaFazla.yukleniyor ? 'Yükleniyor…' : 'Daha eski kayıtları yükle'}
              </button>
            )}
          </div>
        </nav>
      )}
    </div>
  );
}

/** Yan detay çekmecesi (satır ayrıntısı); Esc ve kapat düğmesiyle kapanır. */
export function Cekmece({
  baslik,
  onKapat,
  children,
  eylemler,
}: {
  baslik: ReactNode;
  onKapat: () => void;
  children: ReactNode;
  eylemler?: ReactNode;
}) {
  return (
    <DialogPanel label={typeof baslik === 'string' ? baslik : 'Ayrıntı'} onClose={onKapat} drawer>
      <div className="k-cekmece">
        <header className="k-cekmece-bas">
          <h2>{baslik}</h2>
          <button type="button" onClick={onKapat} aria-label="Kapat">
            ✕
          </button>
        </header>
        <div className="k-cekmece-govde">{children}</div>
        {eylemler && <footer className="k-cekmece-alt">{eylemler}</footer>}
      </div>
    </DialogPanel>
  );
}

export type Islem = { ad: string; onSec: () => void; tehlikeli?: boolean; gizli?: boolean };

/** İşlemler menüsü: formlar sayfaya dökülmez; her işlem kendi penceresini açar. */
export function IslemMenusu({ islemler, ad = 'İşlemler' }: { islemler: Islem[]; ad?: string }) {
  const ref = useRef<HTMLDetailsElement>(null);
  const [sola, setSola] = useState(false);
  const gorunen = islemler.filter((i) => !i.gizli);
  if (gorunen.length === 0) return null;
  return (
    <details
      className={`k-islem-menusu${sola ? ' sola' : ''}`}
      ref={ref}
      onToggle={(e) => {
        // Düğme solda kalırsa (dar ekran, alt satıra kayma) liste sağa açılsın
        const d = e.currentTarget;
        if (d.open) setSola(d.getBoundingClientRect().right < 240);
      }}
    >
      <summary className="k-dugme birincil">{ad} ▾</summary>
      <div className="k-islem-listesi" role="menu">
        {gorunen.map((i) => (
          <button
            key={i.ad}
            type="button"
            role="menuitem"
            className={i.tehlikeli ? 'tehlikeli' : ''}
            onClick={() => {
              ref.current?.removeAttribute('open');
              i.onSec();
            }}
          >
            {i.ad}
          </button>
        ))}
      </div>
    </details>
  );
}

type GerekceIstegi = {
  baslik: string;
  aciklama?: ReactNode;
  onayAdi?: string;
  tehlikeli?: boolean;
  /** Gerekçe zorunlu mu (varsayılan evet; denetim kaydına yazılır). */
  zorunlu?: boolean;
};
const GerekceBaglami = createContext<(p: GerekceIstegi) => Promise<string | null>>(
  async () => null,
);

/** Gerekçe penceresi: `const gerekce = await useGerekce()({...})`; vazgeçilirse null. */
export const useGerekce = () => useContext(GerekceBaglami);

export function GerekceSaglayici({ children }: { children: ReactNode }) {
  const [istek, setIstek] = useState<GerekceIstegi | null>(null);
  const [metin, setMetin] = useState('');
  const coz = useRef<((v: string | null) => void) | null>(null);
  const ref = useRef<HTMLDialogElement>(null);
  const id = useId();
  useEffect(() => {
    if (istek) ref.current?.showModal();
  }, [istek]);
  const bitir = (v: string | null) => {
    ref.current?.close();
    coz.current?.(v);
    coz.current = null;
    setIstek(null);
  };
  const zorunlu = istek?.zorunlu !== false;
  return (
    <GerekceBaglami.Provider
      value={(p) =>
        new Promise((r) => {
          coz.current?.(null);
          coz.current = r;
          setMetin('');
          setIstek(p);
        })
      }
    >
      {children}
      <dialog
        ref={ref}
        className="k-gerekce"
        aria-labelledby={`${id}-b`}
        onKeyDown={trapDialogFocus}
        onCancel={(e) => {
          e.preventDefault();
          bitir(null);
        }}
      >
        {istek && (
          <form
            method="dialog"
            onSubmit={(e) => {
              e.preventDefault();
              if (zorunlu && metin.trim().length < 3) return;
              bitir(metin.trim());
            }}
          >
            <h2 id={`${id}-b`}>{istek.baslik}</h2>
            {istek.aciklama && <div className="k-soluk">{istek.aciklama}</div>}
            <label>
              Gerekçe{zorunlu ? '' : ' (isteğe bağlı)'}
              <textarea
                autoFocus
                rows={3}
                maxLength={300}
                value={metin}
                required={zorunlu}
                minLength={zorunlu ? 3 : undefined}
                onChange={(e) => setMetin(e.target.value)}
              />
            </label>
            <div className="k-eylemler">
              <button type="button" onClick={() => bitir(null)}>
                Vazgeç
              </button>
              <button
                type="submit"
                className={istek.tehlikeli ? 'k-dugme tehlikeli' : 'k-dugme birincil'}
              >
                {istek.onayAdi ?? 'Onayla'}
              </button>
            </div>
          </form>
        )}
      </dialog>
    </GerekceBaglami.Provider>
  );
}

/** Yükleniyor / hata / boş: tek satırlık, sayfayı uzatmayan durum. */
export function Durum({
  yukleniyor,
  hata,
  onTekrar,
}: {
  yukleniyor?: boolean;
  hata?: string | null;
  onTekrar?: () => void;
}) {
  if (hata)
    return (
      <div role="alert" className="k-durum hata">
        <span>{hata.includes('FORBIDDEN') ? 'Bu bölüm için yetkin yok.' : hata}</span>
        {onTekrar && (
          <button type="button" onClick={onTekrar}>
            Yeniden dene
          </button>
        )}
      </div>
    );
  if (yukleniyor)
    return (
      <div role="status" className="k-durum">
        Yükleniyor…
      </div>
    );
  return null;
}
